import { useEffect } from 'preact/hooks'
import type { InventoryButton, UiIntent } from '#contract'
import { useClient } from '../app/client-context.ts'
import { targetOf } from './screen-context.ts'

type Direction = Extract<UiIntent, 'up' | 'down' | 'left' | 'right'>

const STEP: Readonly<Record<Direction, readonly [x: number, y: number]>> = {
  up: [0, -1],
  down: [0, 1],
  left: [-1, 0],
  right: [1, 0],
}

/**
 * Gamepad play on the inventory screen, with DOM focus as the cursor: the D-pad and stick
 * move focus in 2D between every slot and the bin inside `root` (by where they are, so it
 * works side by side and stacked), wrapping within a grid at its edge. A clicks primary, X
 * secondary, Y quick-moves the stack. B and Back close it in the engine.
 */
export function useGridFocus(root: { readonly current: HTMLElement | null }): void {
  const client = useClient()
  useEffect(() => {
    if (!client) return
    return client.onUiIntent((intent) => {
      const container = root.current
      if (!container) return
      const targets = [...container.querySelectorAll<HTMLElement>('[data-target]')]
      const focused = targets.find((element) => element === document.activeElement) ?? null
      if (intent === 'back') return
      if (intent === 'up' || intent === 'down' || intent === 'left' || intent === 'right') {
        const next = focused ? neighbour(focused, targets, intent) : targets[0]
        next?.focus({ focusVisible: true })
        return
      }
      if (!focused) {
        targets[0]?.focus({ focusVisible: true })
        return
      }
      const target = targetOf(focused)
      if (target === null) return
      const button: InventoryButton = intent === 'alternate' ? 'secondary' : 'primary'
      client.inventory.click(target, button, intent === 'quick')
    })
  }, [client, root])
}

/**
 * The nearest target in `direction` from `from`, favouring ones in line with it. With none
 * that way, it wraps: the farthest one back in the same grid's row or column.
 */
function neighbour(
  from: HTMLElement,
  targets: readonly HTMLElement[],
  direction: Direction,
): HTMLElement | null {
  const [dx, dy] = STEP[direction]
  const origin = centre(from)
  let best: HTMLElement | null = null
  let bestScore = Number.POSITIVE_INFINITY
  for (const element of targets) {
    if (element === from) continue
    const c = centre(element)
    const along = (c.x - origin.x) * dx + (c.y - origin.y) * dy
    if (along <= 1) continue
    const across = Math.abs((c.x - origin.x) * dy) + Math.abs((c.y - origin.y) * dx)
    const score = along + across * 2
    if (score < bestScore) {
      bestScore = score
      best = element
    }
  }
  if (best) return best

  const grid = from.parentElement
  const line = targets.filter((element) => {
    if (element === from || element.parentElement !== grid) return false
    const c = centre(element)
    return Math.abs(dx !== 0 ? c.y - origin.y : c.x - origin.x) < 1
  })
  const back = (element: HTMLElement): number => {
    const c = centre(element)
    return -((c.x - origin.x) * dx + (c.y - origin.y) * dy)
  }
  return line.reduce<HTMLElement | null>(
    (far, element) => (far === null || back(element) > back(far) ? element : far),
    null,
  )
}

function centre(element: HTMLElement): { x: number; y: number } {
  const rect = element.getBoundingClientRect()
  return { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 }
}
